import { Link } from '@tanstack/react-router';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';

import type { ARInvoice } from '../../data/mockARData';

interface InvoiceDetailModalProps {
  invoice: ARInvoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: ARInvoice['status'] }) {
  const config: Record<
    ARInvoice['status'],
    { className: string; label: string }
  > = {
    paid: { className: 'bg-green-100 text-green-700', label: 'Paid' },
    unpaid: { className: 'bg-amber-100 text-amber-700', label: 'Unpaid' },
    partial: { className: 'bg-blue-100 text-blue-700', label: 'Partial' },
    overdue: { className: 'bg-red-100 text-red-700', label: 'Overdue' },
  };
  const { className, label } = config[status];
  return <Badge className={className}>{label}</Badge>;
}

export function InvoiceDetailModal({
  invoice,
  open,
  onOpenChange,
}: InvoiceDetailModalProps) {
  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invoice {invoice.invoiceNumber}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary Row */}
          <div className="flex items-center justify-between">
            <div>
              <Link
                to="/ar/collections/$customerId"
                params={{ customerId: invoice.customerId }}
                className="text-sm font-medium hover:text-blue-600 hover:underline"
                onClick={() => onOpenChange(false)}
              >
                {invoice.customerName}
              </Link>
              <p className="text-xs text-muted-foreground">
                {invoice.paymentTerms}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xl font-semibold">
                {formatCurrency(invoice.amount)}
              </p>
              <StatusBadge status={invoice.status} />
            </div>
          </div>

          <Separator />

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Issue Date</p>
              <p className="font-medium">{formatDate(invoice.issueDate)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Due Date</p>
              <p className="font-medium">{formatDate(invoice.dueDate)}</p>
            </div>
          </div>

          <Separator />

          {/* Line Items */}
          <div>
            <p className="text-sm font-medium mb-2">Line Items</p>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs">Description</TableHead>
                    <TableHead className="text-xs text-right w-16">
                      Qty
                    </TableHead>
                    <TableHead className="text-xs text-right w-24">
                      Price
                    </TableHead>
                    <TableHead className="text-xs text-right w-24">
                      Total
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-sm">
                        {item.description}
                      </TableCell>
                      <TableCell className="text-sm text-right">
                        {item.quantity}
                      </TableCell>
                      <TableCell className="text-sm text-right">
                        {formatCurrency(item.unitPrice)}
                      </TableCell>
                      <TableCell className="text-sm text-right font-medium">
                        {formatCurrency(item.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-end pt-2">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-lg font-semibold">
                {formatCurrency(invoice.amount)}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
